import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import PlayerSeasonCharts from "../components/PlayerSeasonCharts";

// Same detection as App.js -- the offline Electron app's local backend has
// no headless-Chrome PDF pipeline and no SMTP configured, so PDF export and
// Email Season Stats can't work there. Everything else on this page already
// works offline today, so only these two buttons are gated, not the page.
const isElectron = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("electron");

// Keep in sync with backend/routes/stats.js's DEFAULT_STATS_EMAIL_SUBJECT/BODY.
const DEFAULT_STATS_EMAIL_SUBJECT = "Your Season Stats — {{name}}";
const DEFAULT_STATS_EMAIL_BODY =
`Hi {{first_name}},

Attached is your full season so far — points, handicap, gross/net and rank, event by event.

Thanks for a great season!`;
const EMAIL_MERGE_TAGS = ["first_name", "name"];

export default function PlayerStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState("avgPoints");
  const [sortDir, setSortDir] = useState("desc");
  const [toast, setToast] = useState("");
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [showChampionship, setShowChampionship] = useState(false);
  const [championship, setChampionship] = useState([]);
  const [championshipLoading, setChampionshipLoading] = useState(false);
  const [showPlayerDetail, setShowPlayerDetail] = useState(false);
  const [playerDetailName, setPlayerDetailName] = useState("");
  const [playerDetailRows, setPlayerDetailRows] = useState([]);
  const [playerDetailLoading, setPlayerDetailLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSelected, setEmailSelected] = useState({});
  const [emailSubject, setEmailSubject] = useState(DEFAULT_STATS_EMAIL_SUBJECT);
  const [emailBody, setEmailBody] = useState(DEFAULT_STATS_EMAIL_BODY);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await api.get("/stats");
      setStats(res.data);
      return res.data;
    } catch(e) { console.error(e); return stats; }
    finally { setLoading(false); }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  // Shared with the export functions below, which re-fetch fresh data
  // immediately before exporting rather than reusing whatever was already in
  // state — otherwise an export triggered from a page left open since before
  // a new round was archived would silently export a stale snapshot missing
  // the newest rounds.
  const sortData = (data) => [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const sorted = sortData(stats);

  const arrow = (col) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const th = (label, col) => (
    <th onClick={() => handleSort(col)}
      className="px-4 py-2 text-center cursor-pointer hover:bg-green-700 select-none">
      {label}{arrow(col)}
    </th>
  );

  const exportToExcel = async () => {
    const fresh = await loadStats();
    const freshSorted = sortData(fresh);
    if (freshSorted.length === 0) { showToast("No stats to export"); return; }
    const sheetData = freshSorted.map(p => ({
      Player: p.name,
      "OOM Points": p.oomPoints,
      Games: p.games,
      "Avg Pts": p.avgPoints,
      "Best Pts": p.bestPoints,
      Wins: p.wins,
      "Top 3": p.top3,
      "Avg Rank": p.avgRank,
      "Nearest Pins": p.nearestPins || 0,
      "Nearest Pin 2s": p.nearestPin2s || 0,
      "Longest Drives": p.longestDrives || 0,
      "Matches Played": p.matchesPlayed || 0,
      "Matches Won": p.matchesWon || 0,
      "Matches Lost": p.matchesLost || 0,
      "Matches Halved": p.matchesHalved || 0,
      "Match Win %": p.matchesPlayed ? p.matchWinPct + "%" : "-",
      "Team Appearances": p.teamAppearances || 0
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Player Stats");
    XLSX.writeFile(wb, "PlayerStats.xlsx");
  };

  const exportToPDF = async () => {
    const fresh = await loadStats();
    const freshSorted = sortData(fresh);
    if (freshSorted.length === 0) { showToast("No stats to export"); return; }
    const doc = new jsPDF();
    let y = 20;
    doc.setFontSize(16);
    doc.text("Player Stats", 14, y);
    y += 10;
    doc.setFontSize(10);

    freshSorted.forEach(p => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFont(undefined, 'bold');
      doc.text(p.name, 14, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      doc.text(`OOM Points: ${p.oomPoints}   Games: ${p.games}   Avg Pts: ${p.avgPoints}   Best Pts: ${p.bestPoints}   Wins: ${p.wins}   Top 3: ${p.top3}   Avg Rank: ${p.avgRank}`, 14, y);
      y += 6;
      doc.text(`Nearest Pins: ${p.nearestPins || 0}   Nearest Pin 2s: ${p.nearestPin2s || 0}   Longest Drives: ${p.longestDrives || 0}`, 14, y);
      y += 6;
      doc.text(`Matches Played: ${p.matchesPlayed || 0}   Won: ${p.matchesWon || 0}   Lost: ${p.matchesLost || 0}   Halved: ${p.matchesHalved || 0}   Win %: ${p.matchesPlayed ? p.matchWinPct + '%' : '-'}   Team Appearances: ${p.teamAppearances || 0}`, 14, y);
      y += 10;
    });

    doc.save("PlayerStats.pdf");
  };

  // Read-only calculation, safe to check at any point in the season, not
  // just once every game is played — best 5 monthly scores per player, ties
  // broken by count of highest-placed finishes among those counting months.
  const loadChampionship = async () => {
    setChampionshipLoading(true);
    try {
      const res = await api.get("/stats/championship");
      setChampionship(res.data);
    } catch(e) { console.error(e); showToast("Failed to load championship standings"); }
    finally { setChampionshipLoading(false); }
  };

  const openChampionship = () => { setShowChampionship(true); loadChampionship(); };

  // Per-player drill-down: their own season history, event by event, for
  // the trend charts below. Reuses the exact same stats_reset_at cutoff and
  // guest-suffix merge as the main leaderboard above, so a player's own
  // chart never disagrees with the season standings on screen.
  const openPlayerDetail = async (name) => {
    setPlayerDetailName(name);
    setShowPlayerDetail(true);
    setPlayerDetailLoading(true);
    try {
      const res = await api.get(`/stats/player/${encodeURIComponent(name)}`);
      setPlayerDetailRows(res.data);
    } catch (e) {
      console.error(e);
      setPlayerDetailRows([]);
      showToast("Failed to load player history");
    } finally {
      setPlayerDetailLoading(false);
    }
  };

  // The charts are Recharts SVGs sized on mount by a ResizeObserver tuned
  // for the live on-screen layout -- that sizing doesn't reliably recompute
  // for a screenshot canvas or a window.print() of the same live layout,
  // which is what broke both of those approaches. Instead, the backend
  // launches a real headless Chrome, loads a dedicated standalone page
  // (a fresh mount with no prior layout to reconcile against) and uses
  // Chrome's own native PDF export -- real chart rendering, no re-layout
  // race, and no dependence on this browser tab's own JS/cache state.
  const [playerDetailExporting, setPlayerDetailExporting] = useState(false);
  const exportPlayerDetailToPDF = async () => {
    if (playerDetailRows.length === 0) return;
    setPlayerDetailExporting(true);
    try {
      const res = await api.post(`/stats/player/${encodeURIComponent(playerDetailName)}/export-pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${playerDetailName.replace(/\s+/g, "_")}_season_stats.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showToast("Failed to export PDF");
    } finally {
      setPlayerDetailExporting(false);
    }
  };

  const openEmailModal = () => {
    // Default: nobody selected. The 14 Oct Hagley send needs a rehearsal
    // with a handful of players first, so starting from an empty selection
    // forces a conscious pick each time rather than an accidental full send.
    setEmailSelected({});
    setEmailResult(null);
    setShowEmailModal(true);
  };
  const emailToggle = (name) => setEmailSelected(s => ({ ...s, [name]: !s[name] }));
  const emailSelectAll = (val) => {
    const next = {};
    stats.forEach(p => { next[p.name] = val; });
    setEmailSelected(next);
  };
  const emailRecipients = stats.filter(p => emailSelected[p.name]).map(p => p.name);

  const sendStatsEmails = async () => {
    if (!emailRecipients.length) return;
    if (!window.confirm(`Email season stats to ${emailRecipients.length} player(s)? This cannot be undone.`)) return;
    setEmailSending(true);
    setEmailResult(null);
    try {
      const res = await api.post("/stats/send-stats-pdfs", {
        playerNames: emailRecipients,
        subject: emailSubject,
        body: emailBody,
      });
      setEmailResult(res.data);
    } catch (e) {
      setEmailResult({ error: "Network error" });
    } finally {
      setEmailSending(false);
    }
  };

  const clearStats = async () => {
    setClearBusy(true);
    try {
      await api.post("/stats/clear", {});
      setShowClearModal(false);
      showToast("Stats cleared — starting a fresh season!");
      loadStats();
    } catch(e) { showToast("Failed to clear stats"); }
    finally { setClearBusy(false); }
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Player Stats</span>
          <span className="text-xs text-gray-500">Based on archived competitions. Click column headers to sort.</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
            <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
            <button onClick={loadStats} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Refresh</button>
            <button onClick={openChampionship} className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold px-3 py-1.5 rounded">🏆 Season Championship</button>
            {!isElectron && <button onClick={openEmailModal} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📧 Email Season Stats</button>}
            <button onClick={() => setShowClearModal(true)} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold px-3 py-1.5 rounded">🗑️ Clear Stats</button>
          </div>
        </div>

        {showClearModal && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"440px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Clear Player Stats?</div>
              <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
                This resets the Player Stats leaderboard to zero for a new season — games, wins, points, nearest pins etc. all start again from scratch.
                <br/><br/>
                <strong>Your archived competitions are not affected</strong> — the Archive page and each player's own results history stay exactly as they are. This only resets what shows on this Stats page.
              </div>
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:"8px",padding:"10px 14px",marginBottom:"16px"}}>
                <div style={{fontSize:"12px",fontWeight:"700",color:"#92400e",marginBottom:"6px"}}>Keep a record of this season first</div>
                <div style={{display:"flex",gap:"8px"}}>
                  <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
                  <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
                </div>
              </div>
              <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
                <button onClick={() => setShowClearModal(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Cancel
                </button>
                <button onClick={clearStats} disabled={clearBusy}
                  style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:clearBusy?0.5:1}}>
                  {clearBusy ? "Clearing..." : "Yes, Clear Stats"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showChampionship && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"640px",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#92400e",marginBottom:"4px"}}>🏆 Season Championship</div>
              <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"16px"}}>
                Each player's best 5 monthly points scores this season. Ties are broken by number of highest-placed finishes among those counting months. Safe to check any time — this isn't a "close the season" action.
              </div>
              <div style={{overflow:"auto",flex:1}}>
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-yellow-700 text-white text-xs sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-center">Pos</th>
                      <th className="px-3 py-2 text-left">Player</th>
                      <th className="px-3 py-2 text-center">Points</th>
                      <th className="px-3 py-2 text-center">Months (best 5 of)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {championship.map((p, i) => (
                      <tr key={p.player_name} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-yellow-50")}>
                        <td className="px-3 py-2 text-center font-bold">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">{p.player_name}</td>
                        <td className="px-3 py-2 text-center font-bold text-yellow-700">{p.points}</td>
                        <td className="px-3 py-2 text-center">{p.monthsCounted} of {p.monthsPlayed}</td>
                      </tr>
                    ))}
                    {!championshipLoading && championship.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-8 text-gray-500">No archived games yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:"16px"}}>
                <button onClick={() => setShowChampionship(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showEmailModal && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"880px",width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:"16px",fontWeight:"700",color:"#6b21a8",marginBottom:"4px"}}>📧 Email Season Stats</div>
              <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"16px"}}>
                Sends each selected player their own season-trend PDF (the same one the 📄 Export PDF button on their chart produces) as an email attachment. Test with a few players first before sending to everyone.
              </div>
              <div style={{display:"flex",gap:"20px",flexWrap:"wrap",overflow:"auto",flex:1}}>
                <div style={{flex:"1 1 380px",minWidth:220}}>
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Subject</label>
                  <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                    style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"10px 12px",fontSize:14,boxSizing:"border-box"}} />
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginTop:14,marginBottom:4}}>Body</label>
                  <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={8}
                    style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"10px 12px",fontSize:14,boxSizing:"border-box",fontFamily:"inherit"}} />
                  <div style={{fontSize:12,color:"#6b7280",marginTop:6}}>
                    Merge tags: {EMAIL_MERGE_TAGS.map(t => <code key={t} style={{background:"#eef2ff",color:"#3730a3",borderRadius:4,padding:"1px 5px",marginRight:6,fontSize:12}}>{`{{${t}}}`}</code>)}
                  </div>
                </div>
                <div style={{flex:"1 1 280px",minWidth:200}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <label style={{fontSize:12,fontWeight:700,color:"#374151"}}>Recipients ({emailRecipients.length} selected)</label>
                    <div>
                      <button onClick={() => emailSelectAll(true)} style={{fontSize:11,border:"1px solid #d1d5db",background:"white",borderRadius:6,padding:"3px 8px",marginLeft:4,cursor:"pointer"}}>All</button>
                      <button onClick={() => emailSelectAll(false)} style={{fontSize:11,border:"1px solid #d1d5db",background:"white",borderRadius:6,padding:"3px 8px",marginLeft:4,cursor:"pointer"}}>None</button>
                    </div>
                  </div>
                  <div style={{maxHeight:280,overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:10}}>
                    {stats.map(p => (
                      <label key={p.name} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderBottom:"1px solid #f3f4f6",fontSize:14}}>
                        <input type="checkbox" checked={!!emailSelected[p.name]} onChange={() => emailToggle(p.name)} />
                        <span style={{flex:1}}>{p.name}</span>
                      </label>
                    ))}
                    {stats.length === 0 && (
                      <div style={{padding:"20px",textAlign:"center",color:"#9ca3af",fontSize:13}}>No archived games yet.</div>
                    )}
                  </div>
                  {emailResult && (
                    <div style={{marginTop:12,fontSize:13}}>
                      {emailResult.error && <div style={{color:"#b91c1c"}}>{emailResult.error}</div>}
                      {emailResult.sent && <div style={{color:"#166534"}}>Sent: {emailResult.sent.length ? emailResult.sent.join(", ") : "none"}</div>}
                      {emailResult.skipped && emailResult.skipped.length > 0 && <div style={{color:"#b91c1c"}}>Skipped (no email on file, or send failed): {emailResult.skipped.join(", ")}</div>}
                    </div>
                  )}
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:16}}>
                <button onClick={() => setShowEmailModal(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Close
                </button>
                <button onClick={sendStatsEmails} disabled={emailSending || !emailRecipients.length}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-5 py-2 rounded"
                  style={{opacity:(emailSending || !emailRecipients.length) ? 0.6 : 1}}>
                  {emailSending ? "Sending… this can take a while (one PDF per player)" : `Send to ${emailRecipients.length} player(s)`}
                </button>
              </div>
            </div>
          </div>
        )}
        {showPlayerDetail && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"1180px",width:"100%",maxHeight:"97vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
              <div style={{overflow:"auto",flex:1}}>
                {playerDetailLoading && (
                  <div className="text-center py-12 text-gray-500 text-sm">Loading {playerDetailName}'s stats…</div>
                )}
                {!playerDetailLoading && playerDetailRows.length === 0 && (
                  <div className="text-center py-12 text-gray-500 text-sm">No archived rounds for {playerDetailName} this season yet.</div>
                )}
                {!playerDetailLoading && playerDetailRows.length > 0 && (
                  <PlayerSeasonCharts playerName={playerDetailName} rows={playerDetailRows} />
                )}
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",gap:"10px",marginTop:"16px"}}>
                {playerDetailRows.length > 0 && !isElectron && (
                  <button onClick={exportPlayerDetailToPDF} disabled={playerDetailExporting}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded"
                    style={{opacity:playerDetailExporting?0.6:1}}>
                    {playerDetailExporting ? "Generating…" : "📄 Export PDF"}
                  </button>
                )}
                <button onClick={() => setShowPlayerDetail(false)}
                  style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Player</th>
                {th("OOM Points", "oomPoints")}
                {th("Games", "games")}
                {th("Avg Pts", "avgPoints")}
                {th("Best Pts", "bestPoints")}
                {th("Wins", "wins")}
                {th("Top 3", "top3")}
                {th("Avg Rank", "avgRank")}
                {th("Nearest Pins", "nearestPins")}
                {th("Nearest Pin 2s", "nearestPin2s")}
                {th("Longest Drives", "longestDrives")}
                {th("Matches Played", "matchesPlayed")}
                {th("Matches Won", "matchesWon")}
                {th("Matches Lost", "matchesLost")}
                {th("Matches Halved", "matchesHalved")}
                {th("Match Win %", "matchWinPct")}
                {th("Team Appearances", "teamAppearances")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <tr key={p.name} onClick={() => openPlayerDetail(p.name)} title="Click for this player's season charts"
                  className={"border-b border-gray-200 cursor-pointer hover:bg-green-100 " + (i%2===0?"bg-white":"bg-green-50")}>
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-center font-bold text-green-700">{p.oomPoints}</td>
                  <td className="px-4 py-2 text-center">{p.games}</td>
                  <td className="px-4 py-2 text-center font-bold text-blue-700">{p.avgPoints}</td>
                  <td className="px-4 py-2 text-center">{p.bestPoints}</td>
                  <td className="px-4 py-2 text-center font-bold text-yellow-600">{p.wins}</td>
                  <td className="px-4 py-2 text-center">{p.top3}</td>
                  <td className="px-4 py-2 text-center">{p.avgRank}</td>
                  <td className="px-4 py-2 text-center">{p.nearestPins || 0}</td>
                  <td className="px-4 py-2 text-center">{p.nearestPin2s || 0}</td>
                  <td className="px-4 py-2 text-center">{p.longestDrives || 0}</td>
                  <td className="px-4 py-2 text-center">{p.matchesPlayed || 0}</td>
                  <td className="px-4 py-2 text-center font-bold text-green-700">{p.matchesWon || 0}</td>
                  <td className="px-4 py-2 text-center">{p.matchesLost || 0}</td>
                  <td className="px-4 py-2 text-center">{p.matchesHalved || 0}</td>
                  <td className="px-4 py-2 text-center font-bold text-blue-700">{p.matchesPlayed ? p.matchWinPct + "%" : "-"}</td>
                  <td className="px-4 py-2 text-center">{p.teamAppearances || 0}</td>
                </tr>
              ))}
              {!loading && stats.length === 0 && (
                <tr><td colSpan={17} className="text-center py-8 text-gray-500">No archived games yet. Archive a competition on the Results screen to see stats.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
